import {
  group,
  groupsCss,
  kitPartCss,
  microHeading,
  microHeadingCss,
  panel,
  panelAction,
  panelActionCss,
  panelCss,
} from '../armKit';
import { searchPath } from '../destinations';
import { fieldFeedWorks } from '../pageData';
import { interests, viewedUser } from '../profileFacts';
import {
  INK,
  matchesBreakpoint,
  mediaCss,
  MUTED,
  WARM_HAIRLINE,
} from '../surface';

import type { ArmContext, ArmSpec } from '../armRegistry';
import type { FeedPaper } from '../pageData';

// Two fields, two papers each: the identity card directly above already lists
// the owner's interests as clickable chips, so a bare search link adds nothing
// the reader doesn't have — the panel earns its rail space by bringing the
// field TO the profile, as actual works by other researchers.
const FIELD_LIMIT = 2;
const PAPERS_PER_FIELD = 2;
const MAX_NAMES = 3;

const ROWS_CLASS = 'profile-liautotest-launchpad__rows';
const AUTHORS_CLASS = 'profile-liautotest-launchpad__authors';
const PAPER_CLASS = 'profile-liautotest-launchpad__paper';

const FIELD_SOURCE = 'profile-liautotest-launchpad-field';
const PAPER_SOURCE = 'profile-liautotest-launchpad-paper';

// Quiet on purpose: the panel previews works, it does not compete with the
// column of them to the right. Authors lead in small semibold muted, and the
// title keeps ink until hovered — the group's one loud element is its More
// action.
function launchpadCss(scope: string): string {
  return `
${scope} .${ROWS_CLASS} {
  list-style: none;
  margin: 0;
  padding: 0;
}
${scope} .${ROWS_CLASS} li {
  padding: 8px 0;
  border-top: 1px solid ${WARM_HAIRLINE};
}
${scope} .${ROWS_CLASS} li:first-child {
  border-top: 0;
}
${scope} .${AUTHORS_CLASS} {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${MUTED};
}
${scope} .${PAPER_CLASS} {
  display: block;
  font-size: 13px;
  line-height: 1.35;
}
${scope} .${PAPER_CLASS}:not(:hover) {
  color: ${INK};
}
`;
}

function armCss(scope: string): string {
  return (
    // The warm frame rather than the bordered card: the beige fill sets the
    // panel apart from the rail's white boxes without adding another rule to
    // a column that is already all rules.
    panelCss(scope, { frame: 'warm', marginBottom: '0' }) +
    // The rail is a wrapping flex row with no gap, so the panel owns its own
    // separation from the identity card above it.
    kitPartCss('panel', scope, 'margin-top: 16px;') +
    // No subtitle by design, so the title's 2px margin — tuned to hand off to
    // one — would run straight into the first eyebrow.
    kitPartCss('panelTitle', scope, 'margin: 0 0 10px;') +
    microHeadingCss(scope) +
    // The kit hairline was tuned against white and vanishes on the warm fill,
    // here between the field groups as on the row separators above.
    groupsCss(scope, { rule: `1px solid ${WARM_HAIRLINE}` }) +
    panelActionCss(scope, { marginTop: '6px' }) +
    launchpadCss(scope) +
    // Banded visibility rather than a load-time mount gate: the panel is its
    // own DOM under its own slot, so nothing strands on narrow, and a resize
    // in either direction lands on the right experience.
    mediaCss('narrow', kitPartCss('panel', scope, 'display: none;'))
  );
}

function authorLine(names: string[]): string {
  if (names.length <= MAX_NAMES) return names.join(', ');

  return `${names.slice(0, MAX_NAMES).join(', ')} et al.`;
}

function paperRow(paper: FeedPaper, ctx: ArmContext): HTMLLIElement {
  const item = document.createElement('li');

  // The authors lead every row by design — they are what says these works are
  // NOT the owner's; a paper the feed names no authors for gets no line at all
  // rather than a guessed one.
  if (paper.authorNames.length > 0) {
    const authors = document.createElement('span');
    authors.className = AUTHORS_CLASS;
    authors.textContent = authorLine(paper.authorNames);
    item.appendChild(authors);
  }

  const title = document.createElement('a');
  title.className = PAPER_CLASS;
  title.href = paper.url;
  title.textContent = paper.title;
  title.addEventListener('click', () => {
    ctx.track(PAPER_SOURCE, { entityId: paper.id });
  });
  item.appendChild(title);

  return item;
}

function fieldGroup(
  field: { id: number; name: string },
  papers: FeedPaper[],
  ctx: ArmContext,
): HTMLElement {
  const section = group('field');
  section.appendChild(microHeading(field.name));

  const rows = document.createElement('ul');
  rows.className = ROWS_CLASS;
  papers.forEach((paper) => rows.appendChild(paperRow(paper, ctx)));
  section.appendChild(rows);

  section.appendChild(
    panelAction(`More in ${field.name}`, searchPath(field.name), () => {
      // No entityId — an interest is not a work. A long name can push the
      // extra over budget, where the tracking ladder sheds it rather than
      // losing the click.
      ctx.track(FIELD_SOURCE, { extra: { interest: field.name } });
    }),
  );

  return section;
}

export const fieldLaunchpadRail: ArmSpec = {
  // stamp stays unset: the gate is the field-feed fetch, which no synchronous
  // prediction can see, so render_state rides markRendered alone.
  onReady: async (ctx) => {
    const user = viewedUser();
    const fields = interests(FIELD_LIMIT);
    if (user == null || fields.length === 0) return;

    const feeds = await Promise.all(
      fields.map((field) => fieldFeedWorks(field.id)),
    );
    const sections = fields.flatMap((field, index) => {
      const papers = feeds[index]
        .filter((paper) => !paper.authorIds.includes(ctx.viewedUserId))
        .slice(0, PAPERS_PER_FIELD);

      return papers.length === 0 ? [] : [{ field, papers }];
    });
    // No field with a usable paper means control: a title over bare search
    // links is exactly the version this rework replaced.
    if (sections.length === 0) return;

    const slot = ctx.mountSlot('railTop');
    if (slot == null) return;

    ctx.injectStyles(armCss(ctx.scope));

    const { root, body } = panel({
      title: `Explore works related to ${user.first_name}'s fields`,
    });

    sections.forEach(({ field, papers }) => {
      body.appendChild(fieldGroup(field, papers, ctx));
    });

    slot.appendChild(root);

    // A panel the band is hiding is not an exposure. Nothing re-runs on
    // resize, so a later resize to wide stays unmarked — the accepted
    // load-time reading, off the same token the sheet's band uses.
    if (matchesBreakpoint('wide')) ctx.markRendered();
  },
};
